import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import { useUpdateUser } from '../hooks/useUserMutations';
import { useUser } from '../hooks/useUsers';
import { UserRoleManager } from './UserRoleManager';
import { UserLabsSection } from './UserLabsSection';
import type { UpdateUserRequest, UserStatus } from '../types/user.types';

const updateUserSchema = z.object({
  fullName: z.string().min(1, 'Name is required').max(100),
  status: z.enum(['active', 'suspended']),
});

type UpdateUserFormData = z.infer<typeof updateUserSchema>;

interface UserEditDialogProps {
  userId: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function UserEditDialog({ userId, open, onClose, onSuccess }: UserEditDialogProps) {
  const { data: user, isLoading } = useUser(userId ?? '');
  const updateUser = useUpdateUser();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UpdateUserFormData>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      fullName: '',
      status: 'active',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        fullName: user.fullName || '',
        status: user.status,
      });
    }
  }, [user, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: UpdateUserFormData) => {
    if (!userId) return;

    try {
      const request: UpdateUserRequest = {
        fullName: data.fullName,
        status: data.status as UserStatus,
      };
      await updateUser.mutateAsync({ id: userId, request });
      handleClose();
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {isLoading ? (
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </DialogContent>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent sx={{ paddingTop: '10px !important' }}>
            <Stack spacing={2}>
              {updateUser.error && (
                <Alert severity="error">
                  {updateUser.error instanceof Error
                    ? updateUser.error.message
                    : 'Failed to update user'}
                </Alert>
              )}
              {user && (
                <>
                  <TextField
                    label="Email"
                    value={user.email}
                    disabled
                    fullWidth
                  />
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Full Name"
                        fullWidth
                        error={!!errors.fullName}
                        helperText={errors.fullName?.message}
                      />
                    )}
                  />
                  <div>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Roles
                    </Typography>
                    <UserRoleManager userId={userId!} />
                  </div>
                  <Divider />
                  <UserLabsSection userId={userId!} currentLabs={user.labs ?? []} />
                  <Divider />
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="suspended">Suspended</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || updateUser.isPending || !isDirty}
            >
              Update User
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
